import { computed } from "vue";
import { useRouter } from "vue-router";
import {
    useSessionStore,
    useCheckoutStore,
    useOrganisationStore,
    usePaymentsStore,
} from "@/plugins/store";
import { routeNames } from "@/constants/routeNames";
import { getPaymentMethodsList } from "@/apis/esendaV2/organisations";
import { configSentryError } from "@/helpers/sentry";
import { redirectToPaymentMethod } from "@/helpers/paymentMethodHelpers";
import { useAlert } from "@esenda/core-ui/composables";

export function useFetchPaymentMethodsAndRedirect() {
    const { showAlert } = useAlert();
    const router = useRouter();
    const sessionStore = useSessionStore();
    const orgStore = useOrganisationStore();
    const checkoutStore = useCheckoutStore();
    const paymentsStore = usePaymentsStore();

    const isLoadingPaymentMethods = computed(
        () => paymentsStore.loadingPaymentMethods,
    );

    const fetchPaymentMethodsAndRedirect = async () => {
        try {
            paymentsStore.loadingPaymentMethods = true;

            /** make sure supplementaryData and amount exist before use in request */
            if (!checkoutStore.supplementaryData) {
                throw new Error(
                    "Cannot fetch new payment methods for expired quote: supplementary data missing from store.",
                );
            }

            const amount = Number(checkoutStore.supplementaryData.amount);

            if (!amount) {
                throw new Error(
                    "Cannot fetch new payment methods for expired quote: payment amount missing from store.",
                );
            }

            /** fetch payment-methods */
            const paymentMethods = await getPaymentMethodsList(
                sessionStore.countryCode ?? "GB",
                amount,
            );

            /** navigate to the Payment Methods page */
            if (paymentMethods !== null) {
                redirectToPaymentMethod(paymentMethods);
            }
        } catch (error) {
            showAlert("alerts.fxQuoteExpiredError");

            configSentryError(
                "fetchPaymentMethodsAndRedirect",
                "fetchPaymentMethodsAndRedirect",
                `Error triggered while attempting to generate new quote: ${JSON.stringify(error, null, 2)}`,
            );

            /** If an error occurs then take the user back */
            router.push({
                name: routeNames.INVOICE,
                params: {
                    shortcode: orgStore.shortcode,
                },
            });
        } finally {
            paymentsStore.loadingPaymentMethods = false;
            checkoutStore.setExpiredQuote(null);
        }
    };

    return { isLoadingPaymentMethods, fetchPaymentMethodsAndRedirect };
}
